import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { isRefusal, refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { ElementKind, ElementView, Library, LibraryInfo, LibrarySearchResult, MappingContent, MatchWord, TermHit } from "../../../api/types";
import { allows } from "../../../app/access";
import { useCurrentMe } from "../../../app/auth";
import { Banner, Button, Card, Dialog, EmptyState, Loading, Pill, ShapeDrawing } from "../../../ui";
import { ElementSearchPicker } from "../components/ElementPicker";
import { FormError, RadioGroup, Select, TextArea, TextInput } from "../components/fields";
import { useCommandId, useRefresh } from "../hooks";
import { elementPath } from "../lib/paths";
import { MATCH_HELP, MATCH_ORDER, MATCH_WORDS, termKindWord } from "../lib/words";

const HALVES = [
  { value: "pid", label: "The P&ID half" },
  { value: "pfd", label: "The PFD half" },
];

/** Component, link and document-link lines are standards lines: how DEXPI stores them. The rest are mapping lines. */
const STANDARDS_KINDS: readonly ElementKind[] = ["component", "link", "document_link"];

/** The screen at /library: the stored standards, one search across them, and a mapping line made from a result. */
export function LibraryPage() {
  const me = useCurrentMe();
  // Reading the libraries is for everyone; making a mapping line or recording "no match" is the engineer's (the server refuses the rest).
  const canMap = allows(me, "engineer");
  const [params, setParams] = useSearchParams();
  const element = params.get("element") ?? "";
  const asked = params.get("q") ?? "";
  const [text, setText] = useState(asked);
  const [library, setLibrary] = useState("");
  const [half, setHalf] = useState<"" | "pid" | "pfd">("");
  const [reading, setReading] = useState<TermHit | null>(null);
  const [mapping, setMapping] = useState<TermHit | null>(null);
  const [noMatch, setNoMatch] = useState(false);

  const libraries = useApiQuery("library_list", {}, { staleTime: 60_000 });
  const search = useApiQuery(
    "library_search",
    { q: asked, library: library || null, half: half || null },
    { enabled: asked.trim() !== "", staleTime: 30_000 },
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (text.trim()) next.set("q", text.trim());
        else next.delete("q");
        return next;
      },
      { replace: true },
    );
  };

  const infoOf = (name: string): LibraryInfo | undefined => libraries.data?.find((l) => l.library === name);

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="library-title">
        <h1 id="library-title">Library browser</h1>
        <p>
          The standards stored in this app, with their licences. Nothing is looked up online: a search reads only what is stored here. A result becomes a mapping line with a match
          word, and &quot;no match&quot; needs a reason.
        </p>
        <form onSubmit={submit} className="library-search" role="search" aria-label="Search the stored libraries">
          <div className="row">
            <TextInput label="Search all the libraries" type="search" value={text} onChange={setText} />
            <Select
              label="In one library"
              value={library}
              placeholder="All of them"
              options={(libraries.data ?? []).map((l) => ({ value: l.library, label: l.library }))}
              onChange={setLibrary}
            />
            <Select label="DEXPI half" value={half} placeholder="Both" options={HALVES} onChange={(value) => setHalf(value as "" | "pid" | "pfd")} />
          </div>
          <Button type="submit" variant="primary" disabled={!text.trim()}>
            Search
          </Button>
        </form>
        {element && (
          <p className="muted">
            Searching for a mapping line for <ElementName id={element} />. <Link to="/library">Stop</Link>
          </p>
        )}
      </Card>

      {asked.trim() !== "" && (
        <Results
          query={asked}
          search={search}
          libraries={libraries.data ?? []}
          canMap={canMap}
          onRead={setReading}
          onMap={setMapping}
          onNoMatch={() => setNoMatch(true)}
        />
      )}

      <Card aria-labelledby="stored-title">
        <h2 id="stored-title">The stored libraries</h2>
        {libraries.isPending ? (
          <Loading>Reading the list of libraries</Loading>
        ) : libraries.isError ? (
          <EmptyState title={isRefusal(libraries.error, "index_missing") ? "The library index is not built yet" : "The libraries could not be listed"}>
            {isRefusal(libraries.error, "index_missing") ? "Build it once from the stored standards, then come back." : refusalText(libraries.error)}
          </EmptyState>
        ) : libraries.data.length === 0 ? (
          <EmptyState title="No library is stored">The stored standards have not been indexed.</EmptyState>
        ) : (
          <ul className="library-list">
            {libraries.data.map((info) => (
              <LibraryCard key={info.library} info={info} />
            ))}
          </ul>
        )}
      </Card>

      {reading && <TermDialog hit={reading} info={infoOf(reading.library)} canMap={canMap} onClose={() => setReading(null)} onMap={() => setMapping(reading)} />}
      {mapping && canMap && (
        <MappingDialog
          hit={mapping}
          info={infoOf(mapping.library)}
          presetElement={element}
          searched={search.data}
          onClose={() => setMapping(null)}
        />
      )}
      {noMatch && canMap && (
        <NoMatchDialog
          libraries={libraries.data ?? []}
          presetElement={element}
          searchText={asked}
          searched={search.data}
          onClose={() => setNoMatch(false)}
        />
      )}
    </div>
  );
}

function ElementName({ id }: { id: string }) {
  const query = useApiQuery("element", { id }, { staleTime: 60_000 });
  const row = query.data?.element;
  return row ? <Link to={elementPath(row)}>{row.name}</Link> : <span className="muted">an element</span>;
}

function LibraryCard({ info }: { info: LibraryInfo }) {
  return (
    <li className="library">
      <h3>
        {info.library} <span className="muted">version {info.version}</span>
      </h3>
      <dl className="facts">
        <div className="facts__row">
          <dt>Licence</dt>
          <dd>{info.licence}</dd>
        </div>
        {info.attribution && (
          <div className="facts__row">
            <dt>Credit</dt>
            <dd>{info.attribution}</dd>
          </div>
        )}
        <div className="facts__row">
          <dt>Source</dt>
          <dd>{info.source}</dd>
        </div>
        <div className="facts__row">
          <dt>Retrieved</dt>
          <dd>{info.retrieved}</dd>
        </div>
        <div className="facts__row">
          <dt>Terms stored</dt>
          <dd>{info.terms}</dd>
        </div>
      </dl>
      {info.no_derivatives && <p className="muted">Its terms are shown and referenced, never copied: the definition is read from the original when it is shown.</p>}
      {!info.shipped && <p className="muted">The library file itself is not stored yet, so a definition may be missing. A mapping line to it is marked unchecked.</p>}
    </li>
  );
}

function Results({
  query,
  search,
  libraries,
  canMap,
  onRead,
  onMap,
  onNoMatch,
}: {
  query: string;
  search: ReturnType<typeof useApiQuery<"library_search">>;
  libraries: LibraryInfo[];
  canMap: boolean;
  onRead: (hit: TermHit) => void;
  onMap: (hit: TermHit) => void;
  onNoMatch: () => void;
}) {
  if (search.isPending) {
    return (
      <Card>
        <Loading>Searching the stored libraries</Loading>
      </Card>
    );
  }
  if (search.isError) {
    return (
      <Card>
        <EmptyState title={isRefusal(search.error, "index_missing") ? "The library index is not built yet" : "The search did not work"}>{refusalText(search.error)}</EmptyState>
      </Card>
    );
  }
  const result: LibrarySearchResult = search.data;
  const names = Object.keys(result.by_library);
  const order = [...libraries.map((l) => l.library as string).filter((n) => names.includes(n)), ...names.filter((n) => !libraries.some((l) => l.library === n))];
  const withHits = order.filter((name) => (result.by_library[name] ?? []).length > 0);
  const empty = libraries.map((l) => l.library as string).filter((n) => !withHits.includes(n));

  return (
    <Card aria-labelledby="results-title">
      <h2 id="results-title">Results for &quot;{query}&quot;</h2>
      {!result.searched_by_meaning && <Banner>Searched by words only. Search by meaning waits until a model is approved, so a missing result is not yet a checked &quot;no match&quot;.</Banner>}
      {withHits.length === 0 ? (
        <EmptyState
          title="Nothing found in the stored libraries"
          action={
            canMap ? (
              <Button variant="secondary" onClick={onNoMatch}>
                Record &quot;no match&quot;
              </Button>
            ) : undefined
          }
        >
          {canMap ? <>Try other words. If nothing fits, record &quot;no match&quot; with the words you searched and the reason.</> : "Try other words."}
        </EmptyState>
      ) : (
        withHits.map((name) => (
          <section key={name} className="result-group" aria-label={`${name} results`}>
            <h3>{name}</h3>
            <ul className="hit-list">
              {(result.by_library[name] ?? []).map((hit) => (
                <HitRow key={`${hit.library}:${hit.term_id}`} hit={hit} canMap={canMap} onRead={() => onRead(hit)} onMap={() => onMap(hit)} />
              ))}
            </ul>
          </section>
        ))
      )}
      {withHits.length > 0 && empty.length > 0 && <p className="muted">Nothing in: {empty.join(", ")}.</p>}
      {withHits.length > 0 && canMap && (
        <p>
          <Button variant="quiet" onClick={onNoMatch}>
            None of these fits: record &quot;no match&quot;
          </Button>
        </p>
      )}
      {result.symbols.length > 0 && (
        <section aria-label="Symbols that match">
          <h3>Symbols of the library</h3>
          <ul className="symbol-grid">
            {result.symbols.map((symbol) => (
              <li key={`${symbol.kind}:${symbol.code}`}>
                <ShapeDrawing shape={symbol.shape} kind={symbol.kind} label={`${symbol.kind === "line_style" ? "Line style" : "Symbol"} ${symbol.code}`} size={48} />
                <code>{symbol.code}</code>
                <span className="muted">{symbol.title}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}

function HitRow({ hit, canMap, onRead, onMap }: { hit: TermHit; canMap: boolean; onRead: () => void; onMap: () => void }) {
  return (
    <li className="hit">
      <div className="hit__head">
        <strong>{hit.label}</strong> <span className="muted">{termKindWord(hit.kind)}</span> {hit.half && <Pill>{hit.half === "pid" ? "P&ID half" : "PFD half"}</Pill>}
        {hit.suggested_word && <Pill>Suggested: {MATCH_WORDS[hit.suggested_word]}</Pill>}
      </div>
      <p className="muted hit__id">
        <code>{hit.term_id}</code> in {hit.library} {hit.version}
      </p>
      {hit.definition ? <p className="hit__definition">{hit.definition}</p> : <p className="muted">{hit.definition_in_original ? "The definition is in the original." : "The library gives no definition."}</p>}
      <div className="row">
        <Button variant="secondary" onClick={onRead} aria-label={`Read it: ${hit.label}`}>
          Read it
        </Button>
        {canMap && (
          <Button variant="primary" onClick={onMap} aria-label={`Make a mapping line: ${hit.label}`}>
            Make a mapping line
          </Button>
        )}
      </div>
    </li>
  );
}

function TermDialog({ hit, info, canMap, onClose, onMap }: { hit: TermHit; info?: LibraryInfo; canMap: boolean; onClose: () => void; onMap: () => void }) {
  const term = useApiQuery("library_term", { library: hit.library, term_id: hit.term_id }, { staleTime: 60_000 });
  const view = term.data;
  const extra = view && view.extra && typeof view.extra === "object" ? Object.entries(view.extra as Record<string, unknown>).filter(([, v]) => typeof v === "string" || typeof v === "number") : [];
  return (
    <Dialog
      open
      onClose={onClose}
      size="large"
      title={hit.label}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {canMap && (
            <Button
              variant="primary"
              onClick={() => {
                onClose();
                onMap();
              }}
            >
              Make a mapping line
            </Button>
          )}
        </>
      }
    >
      {term.isPending && <Loading>Reading the term</Loading>}
      {term.isError && <FormError message={refusalText(term.error)} />}
      {view && (
        <div className="term">
          <p className="muted">
            <code>{view.term_id}</code> in {view.library} {view.version}, {termKindWord(view.kind).toLowerCase()}
            {view.half ? `, ${view.half === "pid" ? "P&ID" : "PFD"} half` : ""}
          </p>
          {view.definition ? (
            <figure className="term__definition">
              <blockquote>{view.definition}</blockquote>
              <figcaption className="muted">The library&apos;s own definition, unchanged.</figcaption>
            </figure>
          ) : (
            <p className="muted">{view.definition_in_original ? "The definition is in the original file. It is not copied into the app." : "The library gives no definition for this term."}</p>
          )}
          {view.parent && <p>Kind of: {view.parent}</p>}
          {view.classes.length > 0 && (
            <div>
              <p>Classes this property is assigned to:</p>
              <ul>
                {view.classes.map((cls) => (
                  <li key={cls}>{cls}</li>
                ))}
              </ul>
            </div>
          )}
          {extra.length > 0 && (
            <dl className="facts">
              {extra.map(([key, value]) => (
                <div key={key} className="facts__row">
                  <dt>{key.replace(/_/g, " ")}</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="muted">Stored in {view.source_file}.</p>
          {info && (
            <p className="muted">
              Licence: {info.licence}. {info.attribution ?? ""}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** The part of a dialog that picks the element a mapping line is for. */
function TargetPicker({ value, onChange, error }: { value: string; onChange: (id: string) => void; error?: string | null }) {
  return <ElementSearchPicker label="The element the line is for" required value={value} error={error} onChange={onChange} hint="Search by name. Items, component kinds, data fields, pick lists and the rest are all here." />;
}

function lineKindFor(kind: string | undefined): "mapping" | "standards" {
  return kind && (STANDARDS_KINDS as readonly string[]).includes(kind) ? "standards" : "mapping";
}

/**
 * The parts of an element a mapping line may be about (CP-15): the values of a pick list, the link slots of a kind of
 * document. Empty for any other element: its line is about the whole element.
 */
export function partOptions(view: ElementView | undefined): Array<{ value: string; label: string }> {
  if (!view) return [];
  const row = view.element;
  if (row.kind === "pick_list") {
    return view.lines
      .filter((line) => line.kind === "pick_value" && line.code && line.state !== "withdrawn" && line.state !== "removed")
      .map((line) => {
        const title = typeof line.content.title === "string" ? line.content.title : "";
        return { value: line.code!, label: title && title !== line.code ? `${title} (${line.code})` : line.code! };
      });
  }
  if (row.kind === "document_kind") {
    const slots = Array.isArray(row.content.link_slots) ? (row.content.link_slots as Array<{ code?: unknown }>) : [];
    return slots.filter((slot) => typeof slot.code === "string" && slot.code).map((slot) => ({ value: String(slot.code), label: String(slot.code) }));
  }
  return [];
}

function MappingDialog({
  hit,
  info,
  presetElement,
  searched,
  onClose,
}: {
  hit: TermHit;
  info?: LibraryInfo;
  presetElement: string;
  searched: LibrarySearchResult | undefined;
  onClose: () => void;
}) {
  const [target, setTarget] = useState(presetElement);
  const [word, setWord] = useState<MatchWord | "">((hit.suggested_word && hit.suggested_word !== "no_match" ? hit.suggested_word : "") as MatchWord | "");
  const [comment, setComment] = useState("");
  const [part, setPart] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const term = useApiQuery("library_term", { library: hit.library, term_id: hit.term_id }, { staleTime: 60_000 });
  const targetRow = useApiQuery("element", { id: target }, { enabled: !!target, staleTime: 30_000 });
  const add = useApiCommand("add_line");
  const ids = useCommandId();
  const refresh = useRefresh();

  const submit = () => {
    const found: Record<string, string> = {};
    if (!target) found.target = "Choose the element the line is for.";
    if (!word) found.word = "Choose the match word.";
    setErrors(found);
    if (Object.keys(found).length > 0 || !word) return;
    const classes = term.data?.classes ?? [];
    const content: MappingContent = {
      library: hit.library as Library,
      library_version: hit.version,
      half: hit.half === "pid" || hit.half === "pfd" ? hit.half : null,
      predicate: word,
      object_id: hit.term_id,
      object_label: hit.label,
      no_match_reason: null,
      words_searched: null,
      libraries_searched: [],
      not_searched_by_meaning_yet: false,
      library_not_loaded: false,
      unchecked: info ? !info.shipped : false,
      library_classes: classes.length > 0 ? classes.join(", ") : null,
      comment: comment.trim() || null,
      case_code: null,
      exchange_note: null,
      derived_not_drawn: false,
    };
    add.mutate(
      {
        id: target,
        line: { client_key: `map_${hit.library}_${hit.term_id}`, kind: lineKindFor(targetRow.data?.element.kind), content, code: part || null },
        command_id: ids.current(),
      },
      {
        onSuccess: () => {
          ids.renew();
          refresh("element", "item_page", "sme_card", "checks_list");
          onClose();
        },
      },
    );
  };

  void searched;
  const parts = partOptions(targetRow.data);
  return (
    <Dialog
      open
      onClose={onClose}
      closeOnBackdrop={false}
      size="large"
      title="Make a mapping line"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={add.isPending} onClick={submit}>
            Add the line
          </Button>
        </>
      }
    >
      <p>
        <strong>{hit.label}</strong> <span className="muted">({hit.term_id} in {hit.library} {hit.version})</span>
      </p>
      <TargetPicker
        value={target}
        onChange={(id) => {
          setTarget(id);
          setPart("");
        }}
        error={errors.target}
      />
      {parts.length > 0 && (
        <Select
          label={targetRow.data?.element.kind === "pick_list" ? "Which value of this list" : "Which link of this kind of document"}
          hint="Leave it as it is when their term matches the whole element."
          value={part}
          placeholder={targetRow.data?.element.kind === "pick_list" ? "The whole list" : "The whole kind of document"}
          options={parts}
          onChange={setPart}
        />
      )}
      <RadioGroup
        legend="How does their term compare with ours?"
        required
        value={word}
        error={errors.word}
        options={MATCH_ORDER.filter((w) => w !== "no_match").map((w) => ({ value: w, label: `${MATCH_WORDS[w]}: ${MATCH_HELP[w]}` }))}
        onChange={(value) => setWord(value as MatchWord)}
      />
      <TextArea label="Comment (optional)" hint="Write down the small difference, if there is one." rows={2} value={comment} onChange={setComment} />
      {term.data && term.data.classes.length > 0 && (
        <p className="muted">The classes this property is assigned to are kept on the line: {term.data.classes.join(", ")}.</p>
      )}
      <FormError message={add.error ? refusalText(add.error) : null} />
    </Dialog>
  );
}

function NoMatchDialog({
  libraries,
  presetElement,
  searchText,
  searched,
  onClose,
}: {
  libraries: LibraryInfo[];
  presetElement: string;
  searchText: string;
  searched: LibrarySearchResult | undefined;
  onClose: () => void;
}) {
  const [target, setTarget] = useState(presetElement);
  const [library, setLibrary] = useState("");
  const [reason, setReason] = useState("");
  const [words, setWords] = useState(searchText);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const targetRow = useApiQuery("element", { id: target }, { enabled: !!target, staleTime: 30_000 });
  const add = useApiCommand("add_line");
  const ids = useCommandId();
  const refresh = useRefresh();

  const submit = () => {
    const found: Record<string, string> = {};
    if (!target) found.target = "Choose the element the line is for.";
    if (!library) found.library = "Choose the library the line is for.";
    if (!reason.trim()) found.reason = "A \"no match\" needs a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const info = libraries.find((l) => l.library === library);
    const content: MappingContent = {
      library: library as Library,
      library_version: info?.version ?? "",
      half: null,
      predicate: "no_match",
      object_id: "sssom:NoTermFound",
      object_label: null,
      no_match_reason: reason.trim(),
      words_searched: words.trim() || null,
      libraries_searched: library ? [library] : [],
      // An early line made from a search by words is not yet a checked "no match".
      not_searched_by_meaning_yet: searched ? !searched.searched_by_meaning : true,
      library_not_loaded: false,
      unchecked: info ? !info.shipped : false,
      library_classes: null,
      comment: null,
      case_code: null,
      exchange_note: null,
      derived_not_drawn: false,
    };
    add.mutate(
      {
        id: target,
        line: { client_key: `nomatch_${library}`, kind: lineKindFor(targetRow.data?.element.kind), content },
        command_id: ids.current(),
      },
      {
        onSuccess: () => {
          ids.renew();
          refresh("element", "item_page", "sme_card", "checks_list");
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      closeOnBackdrop={false}
      size="large"
      title="Record no match"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={add.isPending} onClick={submit}>
            Add the line
          </Button>
        </>
      }
    >
      <p>Nothing in a library fits. The line records the words searched and why, so nobody reads a missing match as a checked one.</p>
      <TargetPicker value={target} onChange={setTarget} error={errors.target} />
      <Select
        label="Which library"
        required
        value={library}
        placeholder="Choose a library"
        options={libraries.map((l) => ({ value: l.library, label: `${l.library} ${l.version}` }))}
        error={errors.library}
        onChange={setLibrary}
      />
      <TextInput label="Words searched" value={words} onChange={setWords} />
      <TextArea label="Why is there no match?" required rows={2} value={reason} error={errors.reason} onChange={setReason} />
      <FormError message={add.error ? refusalText(add.error) : null} />
    </Dialog>
  );
}
